import React, { useRef, useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useReactToPrint } from "react-to-print";
import apiRequest from "../services/api";
import { FaPrint, FaTimes } from "react-icons/fa";
import { numberToWords } from "../utils/numberToWords";

/**
 * Purchase withholding receipt
 * - From  = Tenant (buyer / withholder)  — issues the certificate
 * - To    = Partner/Supplier (withholdee) — receives the certificate
 */
const WithholdingReceipt = ({ isOpen, onClose, invoiceId, type }) => {
  const printAreaRef = useRef();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const normalizedType = String(type || "").toUpperCase();
  const isVatFromProp =
    normalizedType === "VAT" ||
    normalizedType === "VWH" ||
    normalizedType.includes("VAT");

  const fmtDateGC = (dateString) => {
    if (!dateString) return "—";

    const d = new Date(dateString);
    if (isNaN(d.getTime())) return String(dateString);

    const parts = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "Africa/Addis_Ababa",
    }).formatToParts(d);

    const get = (type) => parts.find((p) => p.type === type)?.value || "";

    return `${get("day")}-${get("month")}-${get("year")}`;
  };

  const fmtDateET = (dateString) => {
    if (!dateString) return "—";

    const d = new Date(dateString);
    if (isNaN(d.getTime())) return String(dateString);

    const parts = new Intl.DateTimeFormat("en-ET-u-ca-ethiopic-nu-latn", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "Africa/Addis_Ababa",
    }).formatToParts(d);

    const get = (type) => parts.find((p) => p.type === type)?.value || "";

    return `${get("day")}-${get("month").padStart(2, "0")}-${get("year")}`;
  };

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const fetchDetails = async () => {
      if (!invoiceId) {
        setIsLoading(false);
        setData(null);
        setError("Invoice ID is missing.");
        return;
      }

      setIsLoading(true);
      setError(null);
      setData(null);

      try {
        let apiType = normalizedType;
        if (
          apiType === "WH" ||
          apiType === "WHT" ||
          apiType === "WITHHOLDING"
        ) {
          apiType = "INCOME_TAX";
        } else if (
          apiType === "VWH" ||
          apiType === "VAT_WITHHOLD" ||
          apiType === "VAT_WITHHOLDING"
        ) {
          apiType = "VAT";
        }

        const typeParam = apiType ? `?type=${encodeURIComponent(apiType)}` : "";
        const response = await apiRequest(
          `reports/withholding/receipt/${invoiceId}${typeParam}`
        );

        if (response?.data == null) {
          if (!cancelled) {
            setError("No withholding receipt found.");
            setData(null);
            setIsLoading(false);
          }
          return;
        }

        if (!cancelled) setData(response.data);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.message || err?.message || "Failed to fetch");
          setData(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchDetails();
    return () => {
      cancelled = true;
    };
  }, [invoiceId, isOpen, normalizedType]);

  const handlePrint = useReactToPrint({
    contentRef: printAreaRef,
    documentTitle: data?.receiptNumber || "Withholding Receipt",
    pageStyle: `
      @page { size: A4; margin: 8mm; }
      @media print {
        html, body {
          margin: 0 !important; padding: 0 !important; background: white !important;
          -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
        }
        .a4-print-wrap img[alt="MoR Verification QR"] {
          display: block !important; width: 100px !important; height: 100px !important;
          object-fit: contain !important;
        }
        .no-print { display: none !important; }
      }
    `,
  });

  if (!isOpen) return null;

  const verificationUrl = data?.id
    ? `${window.location.origin}/verify/withholding/${data.id}`
    : window.location.href;

  if (error) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-md p-4">
        <div className="w-full max-w-md bg-slate-800 border border-red-500/30 rounded-xl p-6 text-center shadow-xl">
          <p className="text-red-400 text-base font-semibold mb-4">
            Error: {error}
          </p>
          <button
            onClick={onClose}
            className="px-5 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-md">
        <div className="text-slate-200 text-base font-semibold bg-slate-800 border border-slate-700 p-6 rounded-xl shadow-xl flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
          Loading receipt details...
        </div>
      </div>
    );
  }

  // ---------- helpers ----------
  const fmt = (n, digits = 2) =>
    Number(n || 0).toLocaleString("en-US", {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      return new Intl.DateTimeFormat("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).format(new Date(dateString));
    } catch {
      return dateString;
    }
  };

  const apiType = String(data.type || "").toUpperCase();
  const isVatWithholding =
    isVatFromProp || apiType === "VAT" || apiType.includes("VAT");

  const titleAm = isVatWithholding
    ? "የተ.እ.ታ ተቀናሽ የክፍያ ደረሰኝ"
    : "ከተከፈለ ላይ የገቢ ግብር የተቀነሰ ደረሰኝ";
  const titleEn = isVatWithholding
    ? "VAT withholding on payment"
    : "Withholding tax on payment";

  // Financials
  const taxableBase = Number(data.taxableAmount ?? data.subtotal) || 0;
  const nonTaxableBase = Number(data.nonTaxableAmount) || 0;
  const baseAmount = isVatWithholding
    ? Number(data.taxableAmount ?? data.vatAmount ?? data.tax) || 0
    : taxableBase + nonTaxableBase;

  const rate = Number(data.rate) || 0;
  const withheld = Number(data.amount ?? data.withholdingAmount) || 0;
  const paymentMode =
    data.paymentMode || data.paymentMethod || data.settlement || "N/A";

  const description =
    data.note ||
    data.description ||
    (isVatWithholding
      ? "VAT withholding on payment"
      : "Goods supply — withholding tax on payment");

  const docNo =
    data.receiptCounter ||
    data.receiptNumber ||
    data.documentNumber ||
    `WHT-${data.id || ""}`;
  const irn = data.irn || "—";
  const rrn = data.rrn || "—";

  const printedAt = new Date().toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  // From = Tenant (buyer / withholder)
  // To   = Partner / Supplier (withholdee)
  const fromName = data.tenantName || data.withholderName || "—";
  const fromTin = data.tenantTin || data.withholderTin || "N/A";
  const fromVat = data.tenantVat || data.withholderVat || "N/A";
  const fromSubTin = data.tenantSubTin || data.withholderSubTin || "N/A";
  const fromVatRegDate =
    data.tenantVatRegDate || data.withholderVatRegDate || "N/A";
  const fromCity = data.tenantCity || data.withholderCity || "N/A";
  const fromWoreda = data.tenantWoreda || data.withholderWoreda || "N/A";
  const fromKebele = data.tenantKebele || data.withholderKebele || "N/A";
  const fromHouse = data.tenantHouseNo || data.withholderHouseNo || "N/A";
  const fromPhone = data.tenantPhone || data.withholderPhone || "N/A";
  const fromEmail = data.tenantEmail || data.withholderEmail || "N/A";

  const toName =
    data.partnerName || data.supplierName || data.withholdeeName || "—";
  const toTin =
    data.partnerTin || data.supplierTin || data.withholdeeTin || "N/A";
  const toVat =
    data.partnerVat || data.supplierVat || data.withholdeeVat || "N/A";
  const toSubTin =
    data.partnerSubTin || data.supplierSubTin || data.withholdeeSubTin || "N/A";
  const toVatRegDate =
    data.partnerVatRegDate ||
    data.supplierVatRegDate ||
    data.withholdeeVatRegDate ||
    "N/A";
  const toCity =
    data.partnerCity || data.supplierCity || data.withholdeeCity || "N/A";
  const toWoreda =
    data.partnerWoreda || data.supplierWoreda || data.withholdeeWoreda || "N/A";
  const toKebele =
    data.partnerKebele || data.supplierKebele || data.withholdeeKebele || "N/A";
  const toHouse =
    data.partnerHouseNo ||
    data.supplierHouseNo ||
    data.withholdeeHouseNo ||
    "N/A";
  const toPhone =
    data.partnerPhone || data.supplierPhone || data.withholdeePhone || "N/A";
  const toEmail =
    data.partnerEmail || data.supplierEmail || data.withholdeeEmail || "N/A";

  // ---------- Amharic labels (from official sample) ----------
  const L = {
    documentNumber: "ዶክመንት ቁጥር / Document Number",
    date: "ቀን / Date",
    dateET: "ቀን (ኢት)",
    irn: "የደረሰኝ ቁጥር (IRN) / IRN",
    rrn: "የክፍያ ቁጥር (RRN) / RRN",

    from: "ከ / From",
    to: "ለ / To",
    withholdersTin: "የተቀናሽ ሰሪ ቲን / Withholder's TIN",
    suppliersTin: "የአቅራቢ ቲን / Supplier's TIN",
    vatRegNo: "የተ.እ.ታ ምዝገባ ቁጥር / VAT Reg No",
    subTin: "ንዑስ ቲን / Sub-TIN",
    dateOfVatReg: "የተ.እ.ታ ምዝገባ ቀን / Date of VAT Registration",
    cityTown: "ከተማ / City/Town",
    woreda: "ወረዳ / Woreda",
    kebele: "ቀበሌ / Kebele",
    house: "ቤት ቁጥር / House",
    phone: "ስልክ / Phone",
    email: "ኢሜይል / Email",

    subtotal: "ድምር / Subtotal",
    withholdingAmount: "የተቀነሰ ገቢ ግብር / Withholding Amount",
    description: "መግለጫ / Description",
    amountInWords: "በፊደል / Amount In Words",
    paymentMode: "የክፍያ ዘዴ / Payment Mode",
    printed: "ታትሟል / Printed",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto bg-slate-900/60 backdrop-blur-md">
      <div className="relative w-full max-w-4xl overflow-hidden rounded-2xl bg-slate-800/90 border border-slate-700/50 shadow-2xl p-5">
        {/* Toolbar */}
        <div className="no-print flex items-center justify-between pb-3 mb-3 border-b border-slate-700/50">
          <div>
            <h3 className="text-sm font-bold text-slate-100">
              Withholding Receipt Preview
            </h3>
            <p className="text-xs text-slate-400">
              {titleEn} · {docNo}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold"
            >
              <FaPrint /> Print
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-200 rounded-lg bg-slate-700/50 border border-slate-600/30"
            >
              <FaTimes className="text-sm" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[82vh] pr-1">
          <div
            ref={printAreaRef}
            className="a4-print-wrap bg-white text-black px-3 py-2 font-sans text-[11px] leading-tight"
            style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {data.tenantLogoUrl && (
                  <div className="shrink-0">
                    <img
                      src={data.tenantLogoUrl}
                      alt="Logo"
                      className="w-14 h-14 object-contain"
                    />
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-[14px] font-bold leading-tight">
                    {fromName}
                  </div>
                </div>
              </div>

              <div className="text-right shrink-0 space-y-1">
                <div className="text-[10px]">
                  <span className="font-semibold">{L.documentNumber}: </span>
                  <span className="font-mono font-bold">{docNo}</span>
                </div>
                <div
                  className="qr-box inline-flex items-center justify-center ml-auto p-0 m-0 border-0 bg-white"
                  style={{ width: 100, height: 100, lineHeight: 0 }}
                >
                  {data?.qr ? (
                    <img
                      src={
                        String(data.qr).trim().startsWith("data:")
                          ? String(data.qr).trim()
                          : `data:image/png;base64,${String(data.qr).trim()}`
                      }
                      alt="MoR Verification QR"
                      width={100}
                      height={100}
                      draggable={false}
                      style={{
                        display: "block",
                        width: 100,
                        height: 100,
                        padding: 0,
                        margin: 0,
                        border: 0,
                        objectFit: "fill",
                        imageRendering: "pixelated",
                      }}
                    />
                  ) : (
                    <QRCodeSVG
                      value={verificationUrl}
                      size={100}
                      level="H"
                      includeMargin={false}
                      style={{ display: "block", padding: 0, margin: 0 }}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Title */}
            <div className="text-center my-2">
              <div
                className="text-[13px] font-bold"
                style={{
                  fontFamily:
                    '"Nyala", "Abyssinica SIL", "Ethiopia Jiret", Arial, sans-serif',
                }}
              >
                {titleAm}
              </div>
              <div className="text-[13px] font-bold mt-0.5">{titleEn}</div>
            </div>

            {/* Meta */}
            <div className="text-[10px] space-y-0.5 mb-2">
              <div>
                <span className="font-semibold">{L.date}: </span>
                <span className="font-mono">
                  {fmtDateGC(data.invoiceDate || data.date)}
                </span>
              </div>
              <div>
                <span className="font-semibold">{L.dateET}: </span>
                <span className="font-mono">
                  {fmtDateET(data.invoiceDate || data.date)}
                </span>
              </div>
              <div>
                <span className="font-semibold">{L.irn}: </span>
                <span className="font-mono break-all">{irn}</span>
              </div>
              <div>
                <span className="font-semibold">{L.rrn}: </span>
                <span className="font-mono break-all">{rrn}</span>
              </div>
            </div>

            {/* From / To */}
            <div className="grid grid-cols-2 border border-black mb-2 text-[10px]">
              <div className="border-r border-black p-2 min-w-0">
                <div className="font-bold mb-1">
                  {L.from}: {fromName}
                </div>
                <div className="space-y-0.5 text-slate-800">
                  <div>
                    {L.withholdersTin}:{" "}
                    <span className="font-mono font-semibold">{fromTin}</span>
                  </div>
                  <div>
                    {L.vatRegNo}: <span className="font-mono">{fromVat}</span>
                  </div>
                  <div>
                    {L.subTin}: <span className="font-mono">{fromSubTin}</span>
                  </div>
                  <div>
                    {L.dateOfVatReg}: {fromVatRegDate}
                  </div>
                  <div>
                    {L.cityTown}: {fromCity} · {L.woreda}: {fromWoreda}
                  </div>
                  <div>
                    {L.kebele}: {fromKebele} · {L.house}: {fromHouse}
                  </div>
                  <div>
                    {L.phone}: <span className="font-mono">{fromPhone}</span>
                  </div>
                  <div className="break-all">
                    {L.email}: {fromEmail}
                  </div>
                </div>
              </div>

              <div className="p-2 min-w-0">
                <div className="font-bold mb-1">
                  {L.to}: {toName}
                </div>
                <div className="space-y-0.5 text-slate-800">
                  <div>
                    {L.suppliersTin}:{" "}
                    <span className="font-mono font-semibold">{toTin}</span>
                  </div>
                  <div>
                    {L.vatRegNo}: <span className="font-mono">{toVat}</span>
                  </div>
                  <div>
                    {L.subTin}: <span className="font-mono">{toSubTin}</span>
                  </div>
                  <div>
                    {L.dateOfVatReg}: {toVatRegDate}
                  </div>
                  <div>
                    {L.cityTown}: {toCity} · {L.woreda}: {toWoreda}
                  </div>
                  <div>
                    {L.kebele}: {toKebele} · {L.house}: {toHouse}
                  </div>
                  <div>
                    {L.phone}: <span className="font-mono">{toPhone}</span>
                  </div>
                  <div className="break-all">
                    {L.email}: {toEmail}
                  </div>
                </div>
              </div>
            </div>

            {/* Financials */}
            <table className="w-full border-collapse border border-black text-[10px] mb-2">
              <tbody>
                <tr>
                  <td className="border border-black px-2 py-1.5 font-semibold w-[55%]">
                    {isVatWithholding
                      ? "VAT Amount (base for withholding) / የተ.እ.ታ መጠን"
                      : L.subtotal}
                  </td>
                  <td className="border border-black px-2 py-1.5 text-right font-mono font-semibold">
                    {fmt(baseAmount)} ETB
                  </td>
                </tr>

                {!isVatWithholding && nonTaxableBase > 0 && (
                  <>
                    <tr>
                      <td className="border border-black px-2 py-1.5 text-slate-600">
                        &nbsp;&nbsp;Taxable portion / ታክስ የሚከፈልበት
                      </td>
                      <td className="border border-black px-2 py-1.5 text-right font-mono text-slate-600">
                        {fmt(taxableBase)} ETB
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-black px-2 py-1.5 text-slate-600">
                        &nbsp;&nbsp;Non-taxable portion / ታክስ የማይከፈልበት
                      </td>
                      <td className="border border-black px-2 py-1.5 text-right font-mono text-slate-600">
                        {fmt(nonTaxableBase)} ETB
                      </td>
                    </tr>
                  </>
                )}

                <tr>
                  <td className="border border-black px-2 py-1.5 font-semibold">
                    {L.withholdingAmount}
                    {rate > 0 ? ` (${rate.toFixed(2)}%):` : ":"}
                  </td>
                  <td className="border border-black px-2 py-1.5 text-right font-mono font-bold">
                    {fmt(withheld)} ETB
                  </td>
                </tr>

                <tr>
                  <td className="border border-black px-2 py-1.5 font-semibold">
                    {L.description}:
                  </td>
                  <td className="border border-black px-2 py-1.5 text-right">
                    {description}
                  </td>
                </tr>

                <tr>
                  <td className="border border-black px-2 py-1.5 font-semibold">
                    {L.amountInWords}:
                  </td>
                  <td className="border border-black px-2 py-1.5 text-right font-bold capitalize">
                    {numberToWords(withheld)}
                  </td>
                </tr>

                <tr>
                  <td className="border border-black px-2 py-1.5 font-semibold">
                    {L.paymentMode}:
                  </td>
                  <td className="border border-black px-2 py-1.5 text-right font-bold uppercase">
                    {paymentMode}
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="flex items-center justify-between text-[9px] text-slate-600 pt-2 border-t border-slate-300 mt-4">
              <div>
                {L.printed}: {printedAt}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WithholdingReceipt;
